import { autoinject } from "aurelia-dependency-injection";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { MepDashboard } from "../../tabs/mep-dashboard/mep-dashboard";
import { EmissionsByTypeTableForPrintingViewModel, EmissionsByTypeTableViewModel } from "../../tabs/mep-dashboard/partials/mep-emissions-by-type/mep-emissions-by-type";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { CustomerDetailsModel } from "../../../../../../models/customers/customer.models";
import { EmissionsByCargoTypeTabViewModel } from "../../tabs/mep-dashboard/partials/mep-emissions-by-cargo-type/mep-emissions-by-cargo-type";
import * as Enumerable from "linq";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject
export class PrintMepDashboardDialog {

    public model: MepDashboard;
    public sectionVesselStatistics: boolean = true;
    public sectionTotalEmissions?: boolean = true;
    public sectionVesselCallsByVesselType?: boolean = true;
    public sectionDistinctVesselCountByVesselType?: boolean = true;
    public sectionTopTugboatJobs?: boolean = true;
    public sectionTopContributorsToEmissions?: boolean = true;
    public sectionEmissionsByMonth: boolean = true;
    public sectionEmissionsByPointOfIntrestMap?: boolean = true;
    public sectionEmissionsByPointOfIntrestTable?: boolean = null;
    public sectionEmissionsByVesselType?: boolean = null;
    public sectionEmissionsByOperationMode?: boolean = null;
    public sectionEmissionsByCargoType?: boolean = null;
    public piecharts: HTMLDivElement[] = [];
    public customerDetails: CustomerDetailsModel;
    public emissionsByCargoTypes: EmissionsByCargoTypeTabViewModel[] = []
    public pagedEmissionsByCargoTypes: {
        cargoTypeGroupTable: EmissionsByTypeTableViewModel,
        cargoTypesTable: EmissionsByTypeTableViewModel,
        isExpanded: boolean,
        emissionTypeCategoryId: number,
        isGhgEmissions: boolean
    }[] = [];

    get piechartdivprint() {
        return null;
    }

    set piechartdivprint(value: HTMLDivElement) {
        if (this.piecharts.indexOf(value) < 0)
            this.piecharts.push(value);

        const index = this.piecharts.indexOf(value);

        const activeEmissionsByTypeWithPieCharts = this.model.activeEmissionsByTypeTabViewModels.filter(x => x.pieChartViewModel != null);
        if (activeEmissionsByTypeWithPieCharts[index]?.pieChartViewModel) {
            activeEmissionsByTypeWithPieCharts[index].pieChartViewModel.initEmissionsByTypePieChart(value);
        }
    }

    private _heatmapDiv: HTMLDivElement;
 
    @computedFrom(Reflection.getPropertyPath<PrintMepDashboardDialog>(z=> z._heatmapDiv))
    get printHeatmapDiv() {
        return this._heatmapDiv;
    }

    set printHeatmapDiv(value: HTMLDivElement) {
        this._heatmapDiv = value;
    }

    constructor(private dialogClientService: DialogClientService) { }

    activate(model: MepDashboard) {
        this.model = model;

        //Total emissions
        this.sectionTotalEmissions = this.model.mepSummary.totalEmissionsPerType.length > 0 ? true : null;

        //Vessel statistics
        this.model.vesselStatisticsViewModel.vesselStatisticsItems.forEach(x => {
            if (x.value == 0) {
                if (x.name == "Vessel Calls") {
                    this.sectionVesselCallsByVesselType = null;
                }
                else if (x.name == "Distinct Vessel Count") {
                    this.sectionDistinctVesselCountByVesselType = null;
                }
                else if (x.name == "Tugboat Jobs") {
                    this.sectionTopTugboatJobs = null;
                }
            }
        });

        //Top contributors to emissions
        this.sectionTopContributorsToEmissions = this.model.topContributorsViewModel.hasData ? true : null;

        this.customerDetails = CustomerDetailsModel.getCustomerDetailModelFromRSUser(window["rightshipUser"]);
    }

    attached() {
        const promises = [];

        this.model.emissionsByMonthViewModel.ghgEmissionsChartViewModel.initEmissionsByMonthChart("chartghgdivprint");
        this.model.emissionsByMonthViewModel.pollutantsEmissionsChartViewModel.initEmissionsByMonthChart("chartpollutantsdivprint");
        this.model.vesselStatisticsViewModel.vesselStatisticsItems.forEach(x => x.openVesselTypeDistribution(false));


        if (this.model.selectedTab.heatmapViewModel && this.model.selectedTab.heatmapViewModel.heatmapData)
            this.model.selectedTab.heatmapViewModel.initializeHeatmap(true, this.printHeatmapDiv);

        this.model.activeEmissionsByTypeTabViewModels.forEach(x => {
            if (this.model.selectedTab != x || !this.model.selectedTab.heatmapViewModel || !this.model.selectedTab.heatmapViewModel.heatmapData) {
                const promise = x.getTabData(this.model.filterViewModel.getLastRequest(), true, this.printHeatmapDiv);
                promises.push(promise);
            }
        });

        return Promise.all(promises).then(() => {
            this.sectionEmissionsByPointOfIntrestMap = this.model.activeEmissionsByTypeTabViewModels.filter(x => x.heatmapViewModel).length > 0;
            const selectedEmissionTypes = this.model.filterViewModel.selectedEmissionTypes.length == 0
                ? this.model.filterViewModel.emissionTypes
                : this.model.filterViewModel.selectedEmissionTypes;
            this.model.activeEmissionsByTypeTabViewModels.forEach(x => {
                if (x.tableViewModel) {
                    x.tableForPrintingViewModel = new EmissionsByTypeTableForPrintingViewModel(x.emissionsTabType, selectedEmissionTypes, x.emissionsData);
                }

                //Cargo types ?? filteredItems ??
                if (x.allItems != null && x.allItems.length > 0) {
                    this.sectionEmissionsByCargoType = true;
                    this.emissionsByCargoTypes.push(x);

                    //Find the length of rows & columns
                    x.allItems.forEach(y => {
                        const maxRowSize = 6;
                        const maxColumnSize = 9;

                        //Get number tables needed based on rows/columns
                        let numberOfTablesForRows = 1;
                        let numberOfTablesForColumns = 1;
                        if (y.cargoTypesTable.rows.length > maxRowSize) {
                            numberOfTablesForRows = Math.ceil(y.cargoTypesTable.rows.length / maxRowSize);
                        }
                        if (y.cargoTypesTable.columns.length > maxColumnSize) {
                            numberOfTablesForColumns = Math.ceil(y.cargoTypesTable.columns.length / maxColumnSize);
                        }

                        const cargoType: {
                            cargoTypeGroupTable: EmissionsByTypeTableViewModel,
                            cargoTypesTable: EmissionsByTypeTableViewModel,
                            isExpanded: boolean,
                            emissionTypeCategoryId: number,
                            isGhgEmissions: boolean
                        } = y;

                        for (let i = 0; i < numberOfTablesForColumns; i++) {

                            //Insert columns
                            const cargoTypeGroupTableColums = Enumerable.from(cargoType.cargoTypeGroupTable.columns).skip(i * maxColumnSize).take(maxColumnSize).toArray();
                            const cargoTypeTableColumns = Enumerable.from(cargoType.cargoTypesTable.columns).skip(i * maxColumnSize).take(maxColumnSize).toArray();

                            for (let j = 0; j < numberOfTablesForRows; j++) {

                                const newCargoType: {
                                    cargoTypeGroupTable: EmissionsByTypeTableViewModel,
                                    cargoTypesTable: EmissionsByTypeTableViewModel,
                                    isExpanded: boolean,
                                    emissionTypeCategoryId: number,
                                    isGhgEmissions: boolean
                                } = CloneHelper.clone(y);

                                newCargoType.cargoTypeGroupTable.columns = cargoTypeGroupTableColums;
                                newCargoType.cargoTypesTable.columns = cargoTypeTableColumns;
                                newCargoType.cargoTypesTable.rows = Enumerable.from(newCargoType.cargoTypesTable.rows).skip(j * maxRowSize).take(maxRowSize).toArray();
                                newCargoType.cargoTypeGroupTable.rows.forEach(z => {
                                    z.cells = Enumerable.from(z.cells).skip(i * maxColumnSize).take(maxColumnSize).toArray();
                                }); 
                                newCargoType.cargoTypesTable.rows.forEach(z => {
                                    z.cells = Enumerable.from(z.cells).skip(i * maxColumnSize).take(maxColumnSize).toArray();
                                }); 
                                
                                newCargoType.cargoTypeGroupTable.getEmissionsBubbleSize = y.cargoTypeGroupTable.getEmissionsBubbleSize;
                                newCargoType.cargoTypesTable.getEmissionsBubbleSize = y.cargoTypesTable.getEmissionsBubbleSize;

                                this.pagedEmissionsByCargoTypes.push(newCargoType);
                            }
                        }
                    })
                }

                if (x.tableViewModel != null || x.pieChartViewModel != null) {
                    if (x.title == "Emissions by Point of Interest") {
                        this.sectionEmissionsByPointOfIntrestTable = true;
                    }
                    else if (x.title == "Emissions by Vessel Type") {
                        this.sectionEmissionsByVesselType = true;
                    }
                    else if (x.title == "Emissions by Operation Mode") {
                        this.sectionEmissionsByOperationMode = true;
                    }
                }

                if (x.heatmapViewModel && x.heatmapViewModel.heatmapData)
                    x.heatmapViewModel.initializeHeatmap(true, this.printHeatmapDiv);
            });
        })
    }

    close() {
        this.clearPrintingData();
        this.dialogClientService.hide();
    }

    /**
     * Get units value
     * @param isGhgEmissions
     */
    getUnits = (isGhgEmissions: boolean) => isGhgEmissions ? 'GHG Emissions (tCO<sub>2</sub>e_100yr)' : 'Emissions (tonnes)';

    printDocument() {
        //If the current user is a customer with a trial account
        if (this.customerDetails?.isMepTrial) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.TrialVersionDialog, { customerId: this.customerDetails.customerId, title: 'Print Report' });
        }
        else {
            $(".document-port-dashboard").printThis({
                importCSS: true,
                importStyle: true,
                loadCSS: "/styles/core.min.css",
                canvas: true
            });
        }
    }

    showVesselStatstics(name: string, value: number) {
        if (value && value !== 0) {
            if (name == "Vessel Calls" && this.sectionVesselCallsByVesselType) {
                return true;
            }
            else if (name == "Distinct Vessel Count" && this.sectionDistinctVesselCountByVesselType) {
                return true;
            }
            else if (name == "Tugboat Jobs" && this.sectionTopTugboatJobs) {
                return true;
            }
        }
        return false;
    }

    showEmissionsBy(name: string) {
        if (name == "Emissions by Point of Interest" && this.sectionEmissionsByPointOfIntrestTable) {
            return true;
        }
        else if (name == "Emissions by Vessel Type" && this.sectionEmissionsByVesselType) {
            return true;
        }
        else if (name == "Emissions by Operation Mode" && this.sectionEmissionsByOperationMode) {
            return true;
        }
        return false;
    }

    clearPrintingData() {
        if (this.model && this.model.selectedTab && this.model.selectedTab.heatmapViewModel)
            this.model.selectedTab.heatmapViewModel.clearPrintMap();

        this.model.activeEmissionsByTypeTabViewModels.forEach(tab => {
            tab.tableForPrintingViewModel = null;
        })
    }
}
